import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { AuthContext } from "./components/AuthContext";
import { getCurrentUser, login, logout } from "./queries";
import { ILoginCredentials } from "./types";

export const AUTH_QUERY_KEYS = {
    currentUser: ["auth", "current-user"] as const,
} as const;

/**
 * Fetches the current authenticated user.
 * Returns null when no session is active (401 handled in getCurrentUser query fn).
 *
 * @internal — consumed by AuthProvider only. External consumers should use
 * useAuth() which reads from context rather than opening a second query subscription.
 */
export const useCurrentUser = () =>
    useQuery({
        queryKey: AUTH_QUERY_KEYS.currentUser,
        queryFn: getCurrentUser,
        retry: false,
        refetchOnWindowFocus: true,
    });

export const useLogin = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (credentials: ILoginCredentials) => login(credentials),
        onSuccess: (authenticatedUser) => {
            // Synchronously populate the cache so ProtectedRoute sees the session
            // immediately when navigate("/") fires, without waiting for an async refetch.
            queryClient.setQueryData(AUTH_QUERY_KEYS.currentUser, authenticatedUser);
        },
    });
};

export const useLogout = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: logout,
        onSuccess: () => {
            // setQueryData is sufficient — no invalidateQueries needed here.
            // invalidating would trigger an unnecessary server refetch for a session
            // we have just explicitly ended.
            queryClient.setQueryData(AUTH_QUERY_KEYS.currentUser, null);
        },
    });
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
